import { MetricType as MetricTypeClient } from '@prisma-clinic/client'
import { match } from 'ts-pattern'

import { MealSituation, MetricLevel, MetricType } from './types'

const FOR_HUMANS = {
    [MetricType.BloodOxygen]: 'common.metricTypes.metricTypeSPO2',
    [MetricType.BloodPressure]: 'common.metricTypes.metricTypeBP',
    [MetricType.BloodSugar]: 'common.metricTypes.metricTypeGLUCOSE',
    [MetricType.BodyHeight]: 'common.metricTypes.metricTypeHEIGHT',
    [MetricType.BodyMassIndex]: 'common.metricTypes.metricTypeBMI',
    [MetricType.BodyTemperature]: 'common.metricTypes.metricTypeTEMPERATURE',
    [MetricType.BodyWeight]: 'common.metricTypes.metricTypeWEIGHT',
    [MetricType.DailySteps]: 'common.metricTypes.metricTypeSTEPS',
    [MetricType.FoetalHeartRate]: 'common.metricTypes.metricTypeFOETAL_HR',
    [MetricType.FoetalHeartRateVariability]: 'common.metricTypes.metricTypeFOETAL_HRV',
    [MetricType.HeartRateVariability]: 'common.metricTypes.metricTypeHRV',
    [MetricType.RestingHeartRate]: 'common.metricTypes.metricTypeRESTING_HR',
    [MetricType.SleepTime]: 'common.metricTypes.metricTypeSLEEP',
    [MetricType.WaterIntake]: 'common.metricTypes.metricTypeWATER_INTAKE',
    [MetricType.MedicationIntake]: 'common.metricTypes.metricTypeMEDICATION_INTAKE',
    [MetricType.UterineActivity]: 'common.metricTypes.metricTypeUTERINE',
    [MetricType.Arrhythmia]: 'common.metricTypes.metricTypeARRHYTHMIA',
    [MetricType.Mood]: 'common.metricTypes.metricTypeMOOD',
    [MetricType.AbdominalCircumference]: 'common.metricTypes.metricTypeABDOMINAL_CIRCUMFERENCE',
    [MetricLevel.Normal]: 'common.alertLevels.ok',
    [MetricLevel.WarningAbove]: 'common.alertLevels.warning',
    [MetricLevel.WarningBelow]: 'common.alertLevels.warning',
    [MetricLevel.AlertAbove]: 'common.alertLevels.alert',
    [MetricLevel.AlertBelow]: 'common.alertLevels.alert',
    [MealSituation.Fasting]: 'common.mealSituations.fasting',
    [MealSituation.BeforeMeal]: 'common.mealSituations.beforeMeal',
    [MealSituation.AfterMeal]: 'common.mealSituations.afterMeal',
    [MealSituation.Random]: 'common.mealSituations.random',
    insulin: 'common.health.insulin',
} as const

export type ValueRangeThreshold = {
    type: 'value_range'
    metricType: string
    alertAbove?: number
    alertBelow?: number
    warningAbove?: number
    warningBelow?: number
}

export type MetricEvaluation = {
    value?: number
    systolic?: number
    diastolic?: number
    beforeMeal?: number
    afterMeal?: number
}

export const forHumans = (type: MetricType | MetricLevel | MealSituation | 'insulin'): string =>
    FOR_HUMANS[type]

export const getMetricTypeChoices = (selected: MetricType[] | undefined) =>
    Object.values(MetricType)
        .map((metricType) => ({
            key: metricType,
            text: forHumans(metricType),
            isSelected: !!selected?.includes(metricType),
        }))
        .sort(({ text: a }, { text: b }) => a.localeCompare(b))

export function metricTypeToKeys(type: MetricTypeClient): string[] {
    return match(type)
        .with(MetricTypeClient.BP, () => ['bloodPressureSystolic', 'bloodPressureDiastolic'])
        .with(MetricTypeClient.GLUCOSE, () => ['glucoseBeforeMeal', 'glucoseAfterMeal'])
        .with(MetricTypeClient.WEIGHT, () => ['bodyWeight'])
        .with(MetricTypeClient.TEMPERATURE, () => ['bodyTemperature'])
        .with(MetricTypeClient.SPO2, () => ['spo2'])
        .with(MetricTypeClient.RESTING_HR, () => ['restingHeartRate'])
        .with(MetricTypeClient.STEPS, () => ['dailySteps'])
        .with(MetricTypeClient.SLEEP, () => ['sleep'])
        .with(MetricTypeClient.WATER_INTAKE, () => ['waterIntake'])
        .otherwise(() => [])
}
